import { AutomationsIcon } from "@minket/brand/icons/automations";
import { PublishIcon } from "@minket/brand/icons/publish";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { UnavailableAction } from "@/components/page/action";
import { GhostSections } from "@/components/page/ghost";
import { DetailPage } from "@/components/page/item";
import { Page } from "@/components/page/page";
import { LoadingState } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/automations/$id")({
  component: AutomationDetail,
});

// The builder is exempt from the route budget, like the block editor; it loads once the
// automation has.
const AutomationBuilder = lazy(() =>
  import("@/components/automations/automation-builder").then((m) => ({
    default: m.AutomationBuilder,
  })),
);

const sections = [
  { title: "Details", description: "" },
  { title: "Builder", description: "", wide: true },
  { title: "Runs", description: "" },
  {
    title: "Versions",
    description: "New runs start on the published version; runs in flight finish on theirs.",
  },
] as const;

function AutomationDetail() {
  const { id } = Route.useParams();
  const { project } = useWorkspace();
  const automation = useQuery({
    ...apiQuery(endpoints.automation, { projectId: project?.id ?? "", automationId: id }),
    enabled: project !== null,
  });

  if (project && automation.isSuccess) {
    return (
      <Suspense
        fallback={
          <Page
            title={automation.data.name}
            documentTitle={`${automation.data.name} · Automations`}
            serial={{ id, label: "automation id" }}
            back={{ to: "/automations", label: "Automations" }}
            action={
              <UnavailableAction label="Publish" icon={PublishIcon} reason="Loading the builder" />
            }
          >
            <LoadingState label="Loading the builder">
              <GhostSections sections={sections} busy />
            </LoadingState>
          </Page>
        }
      >
        <AutomationBuilder
          key={automation.data.id}
          projectId={project.id}
          automation={automation.data}
        />
      </Suspense>
    );
  }

  return (
    <DetailPage
      kind="Automation"
      icon={AutomationsIcon}
      id={id}
      back={{ to: "/automations", label: "Automations" }}
      action={{ label: "Publish", icon: PublishIcon }}
      noun="this automation"
      will="This page will hold the visual builder, the automation's versions and its runs with their step history."
      sections={sections}
      query={(projectId) => apiQuery(endpoints.automation, { projectId, automationId: id })}
    />
  );
}
